import { useEffect, useState } from "react";
import { Loader2, Save, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * AmbassadorSocialsEditor — admin Ambassadors modal helper.
 * Lets an admin override locked social handles via PATCH /admin/ambassadors/{id}/socials.
 * Use sparingly — every change should be requested by the ambassador in writing.
 */
const FIELDS = [
  { k: "dashboard_handle", label: "Public handle (leaderboard)" },
  { k: "instagram_handle", label: "Instagram handle" },
  { k: "linkedin_url", label: "LinkedIn URL" },
  { k: "youtube_url", label: "YouTube URL" },
  { k: "twitter_handle", label: "Twitter handle" },
];

export default function AmbassadorSocialsEditor({ ambassador, onUpdated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      dashboard_handle: ambassador?.dashboard_handle || "",
      instagram_handle: ambassador?.instagram_handle || "",
      linkedin_url: ambassador?.linkedin_url || "",
      youtube_url: ambassador?.youtube_url || "",
      twitter_handle: ambassador?.twitter_handle || "",
    });
  }, [ambassador?.id]);

  const dirty = FIELDS.some((f) => (form[f.k] || "") !== (ambassador?.[f.k] || ""));

  const save = async () => {
    const patch = {};
    FIELDS.forEach((f) => {
      const next = (form[f.k] || "").trim();
      if (next !== (ambassador?.[f.k] || "")) patch[f.k] = next;
    });
    if (Object.keys(patch).length === 0) return;
    try {
      setSaving(true);
      await api.patch(`/admin/ambassadors/${ambassador.id}/socials`, patch);
      toast.success("Socials updated");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="amb-socials-editor">
      <div className="flex items-center gap-2 text-amber-300">
        <ShieldAlert className="h-4 w-4" />
        <p className="text-xs font-bold uppercase tracking-wider">Override locked social handles</p>
      </div>
      <p className="mt-1 text-[11px] text-amber-200/70">Ambassadors can&apos;t edit these themselves — use only on a written request from the ambassador. Every change is logged via Mongo&apos;s document history.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <label key={f.k} className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">{f.label}</span>
            <input
              data-testid={`amb-socials-${f.k}`}
              value={form[f.k] ?? ""}
              onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm font-mono text-zinc-100 focus:border-[#A855F7] focus:outline-none"
            />
          </label>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="amb-socials-save"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-1.5 text-sm font-bold text-white disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save override
        </button>
      </div>
    </div>
  );
}
